import { ErrorHandler, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { MessageService } from '@shared/services/message.service';
import { FirebaseAuthErrors } from '@app/modules/authentication/login-stepper/firebase-auth-errors';
import { TubErrorReportingService } from '@shared/services/tub-error-reporting.service';
import { UserCancelledOAuthError } from './user-cancelled-oauth-error';
import { environment } from '@environments/environment';

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- provided via useClass token in app.config.ts
@Injectable()
export class DashboardErrorHandler implements ErrorHandler {
  private messageService = inject(MessageService);

  private tubErrorReportingService: TubErrorReportingService = inject(TubErrorReportingService);

  /**
   * Handles errors in the dashboard and reports them via TUB if they are uncaught.
   * @param error
   */
  public handleError(error: Error | HttpErrorResponse): void {
    // User-cancelled Google OAuth popup — a deliberate user action, not an error
    if (error instanceof UserCancelledOAuthError) {
      return;
    }

    // Firebase login errors handled in login stepper
    if (this.isFirebaseAuthError(error)) {
      // Do nothing for Firebase auth errors as they're handled elsewhere
      return;
    }

    if (this.isFirestorePermissionError(error)) {
      // Silently ignore Firestore permission errors
      return;
    }

    if (this.isFirestoreShutDownError(error)) {
      // Silently ignore Firestore shutdown errors
      return;
    }

    // Handle all other errors. Outside production, also log to the console — the toast is generic
    // and the TUB report is not visible to a developer debugging locally. Suppressed in production:
    // the error text can embed patient-identifying data that should not surface in devtools, and the
    // TUB report already captures it for later inspection.
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.error('Dashboard uncaught exception:', error);
    }
    this.messageService.showMessage('Error: Sorry, something went wrong - please try again.');
    this.tubErrorReportingService.send('Dashboard Uncaught exception', error);
  }

  /**
   * Determines if the given error is related to Firebase authentication errors.
   * This can occur on login/logout so suppresses these errors.
   */
  private isFirebaseAuthError(error: Error): boolean {
    for (const authErr of Object.keys(FirebaseAuthErrors)) {
      if (error && error.message && error.message.includes(FirebaseAuthErrors[authErr])) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the error is a Firestore permission error.
   * These errors occur when trying to access Firestore resources that the user no longer has permission to,
   * such as after a patient is discharged.
   */
  private isFirestorePermissionError(error: Error): boolean {
    // Check if it's a Firebase error with a permission-related message
    return error && error.name === 'FirebaseError' && error.message && error.message.includes('permission');
  }

  /**
   * Checks if the error is a Firestore shutdown error.
   * This sometimes occurs when the dashboard is in the process of logging out, so can be suppressed through this
   * method.
   */
  private isFirestoreShutDownError(error: Error): boolean {
    return error && error.name === 'FirebaseError' && error.message && error.message.includes('shutting down');
  }
}
